import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { z } from 'zod';
import { sqlDateTimeParser, transformSQLDateTime } from '../core/phpTypes';

export const fullReservationParser = z
	.object({
		id: z.coerce.string(),
		created: sqlDateTimeParser.transform(transformSQLDateTime).optional(),
		updated: sqlDateTimeParser.transform(transformSQLDateTime).optional(),
		start: sqlDateTimeParser.transform(transformSQLDateTime),
		end: sqlDateTimeParser.transform(transformSQLDateTime),
		locationId: z.number(),
		users: z.array(
			z.object({
				userId: z.number(),
				accepted: z.boolean(),
			}),
		),
		canceledBy: z.number().nullable(),
		approved: sqlDateTimeParser
			.nullable()
			.transform((d) => (d != undefined ? transformSQLDateTime(d) : null)),
		notes: z.string().nullable(),
	})
	.transform((r) => ({
		...r,
		anonymized: false as const,
	}));

export type FullReservation = z.infer<typeof fullReservationParser>;

export const anonReservationParser = z.object({
	anonymized: z.literal(true),
	id: z.string(),
	start: sqlDateTimeParser.transform(transformSQLDateTime),
	end: sqlDateTimeParser.transform(transformSQLDateTime),
	locationId: z.number().optional(),
	users: z.array(
		z.object({
			userId: z.number(),
			accepted: z.boolean(),
		}),
	),
});

export type AnonReservation = z.infer<typeof anonReservationParser>;

export const reservationParser = z.union([
	anonReservationParser, // keep as first to avoid bug where we fall in the first branch by default
	fullReservationParser,
]);

export type Reservation = z.infer<typeof reservationParser>;

export const allReservationsAtom = atom<Reservation[]>([]);

export const reservationAtom = atomFamily((id: string) =>
	atom(
		(get) => get(allReservationsAtom).find((r) => r.id == id),
		(get, set, value: Reservation) => {
			set(allReservationsAtom, (reservations) => [
				...reservations.filter((r) => r.id != id),
				value,
			]);
		},
	),
);

export const userReservationsAtom = atomFamily((userId: number) =>
	atom((get) =>
		get(allReservationsAtom).filter((r) =>
			r.users.some((u) => u.userId == userId),
		),
	),
);

export const locationReserationsAtom = atomFamily((locationId: number) =>
	atom((get) =>
		get(allReservationsAtom).filter((r) => r.locationId == locationId),
	),
);

// merge a1 and a2 into a new array with priority given to elements of a2
export const mergeReservations = (a1: Reservation[], a2: Reservation[]) => {
	return [...a1.filter((r) => !a2.some((r2) => r2.id == r.id)), ...a2];
};
